import React, { useEffect, useRef, createContext, useContext, ReactNode } from 'react'
import { decodeId, encodeId } from '../../helpers'
import { useAuth } from '../auth-context'
import { useSocket } from '../socket-context'
import { InitialLoadQuery, InitialLoadQueryVariables } from '../../graphql/core-types'
import { useQuery } from '@apollo/client/react'
import { initialLoad } from '../../graphql/core/queries/initial-load'

export enum LogOutReason {
  NORMAL = 'normal_logout',
  NOT_AUTHENTICATED = 'not_authenticated',
}

type GlobalContextData = InitialLoadQuery

const PARLAY_EVENTS = ['parlay_cancelled', 'parlay_completed', 'parlay_updated', 'parlay_adjusted']

// @ts-ignore
export const GlobalContext = createContext<GlobalContextData>(null)

interface UserProviderProps {
  children: ReactNode
}

export const GlobalProvider: React.FC<UserProviderProps> = ({
  children,
}) => {
  const { userId } = useAuth()
  const { setUserIdForSocket, userChannel, stakesChannel, setTeamIdForSocket } = useSocket()

  const { data, refetch } = useQuery<InitialLoadQuery, InitialLoadQueryVariables>(initialLoad, 
    {
      variables: { id: encodeId('User', userId ?? ''), userId: encodeId('User', userId ?? '') },
      skip: !userId,
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  )

  const globalState = data || null

  const currentUserId = globalState && globalState?.user?.id ? decodeId(globalState?.user?.id) : null
  const currentTeamId = globalState && globalState?.user?.activeTeam?.id ? decodeId(globalState?.user.activeTeam.id) : null

  useEffect(() => {
    setTeamIdForSocket(currentTeamId)
    setUserIdForSocket(currentUserId)
  }, [currentUserId])

  useEffect(() => {
    if (globalState === null && userId) void refetch()
  }, [globalState, userId])

  useEffect(() => {
    if(!userChannel) return
    const channelRef = userChannel.on('new_team', ({team_id}: {team_id: string}) => {
      setTeamIdForSocket(team_id)
      void refetch()
    })

    const betUpdateChannelRef = userChannel.on(
      'bet_updated',
      async () => {
        void refetch();
      }
    )
    const betadjusted = userChannel.on(
      'user_bet_adjusted',
      async () => {
        void refetch();
      },
    )

    const betPlacedChannelRef = userChannel.on(
      'bet_placed',
      async () => {
        void refetch();
      },
    )

    // parlay lifecycle events plc-app listens for, so cancelled or settled parlays drop off without waiting for bet_updated
    const parlayEventRefs = PARLAY_EVENTS.map((event) => ({
      event,
      ref: userChannel.on(event, () => {
        void refetch();
      }),
    }))

    return () => {
      userChannel.off('new_team', channelRef)
      userChannel.off('bet_updated', betUpdateChannelRef)
      userChannel.off('user_bet_adjusted', betadjusted)
      userChannel.off('bet_placed', betPlacedChannelRef)
      parlayEventRefs.forEach(({ event, ref }) => userChannel.off(event, ref))
    }
  }, [userChannel])

  // stakes is one shared channel for every offer, so only refetch when it's one of this streamer's bets.
  // that's how a new staker shows up on the bet card
  const stakeOfferIds = new Set(
    [...(data?.pendingBets?.betSlips ?? []), ...(data?.getBetHistory?.betSlips ?? [])]
      .map((betSlip) => betSlip?.stakeOffer?.id)
      .filter((id): id is string => Boolean(id))
  )
  const stakeOfferIdsRef = useRef(stakeOfferIds)
  stakeOfferIdsRef.current = stakeOfferIds

  useEffect(() => {
    if (!stakesChannel) return
    const ref = stakesChannel.on('stake_offer_updated', ({ id }: { id: string }) => {
      const offerIds = stakeOfferIdsRef.current
      // the payload id may be raw or the base64 graphql id
      if (offerIds.has(id) || Array.from(offerIds).some((offerId) => decodeId(offerId) === id)) void refetch()
    })
    return () => stakesChannel.off('stake_offer_updated', ref)
  }, [stakesChannel])

  if(!globalState) return null
  return (
    <GlobalContext.Provider value={globalState} >
      {children}
    </GlobalContext.Provider>
  )
}

export const useGlobal = (): GlobalContextData => useContext(GlobalContext)
